import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Card, Input, Label } from '@features/ui';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const schema = yup.object({
  login: yup.string().required('Email is required'),
  password: yup.string().required('Password is required'),
});

export type SignInFormData = yup.InferType<typeof schema>;

type Props = {
  className?: string;
  onSubmit: (data: SignInFormData) => void;
  onOpenForgotEmail: () => void;
  onOpenForgotPassword: () => void;
  hideRedirectToSignUp?: boolean;
};

export const SignInForm: React.FC<Props> = ({
  className,
  onSubmit,
  onOpenForgotEmail,
  onOpenForgotPassword,
  hideRedirectToSignUp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      login: '',
      password: '',
    },
  });

  return (
    <form
      autoComplete="off"
      className={'w-full'}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Card
        className={`py-8 pr-8 pl-8 xl:py-12.5 xl:pl-23.25 xl:pr-21.75 w-full lg:w-[584px] ${className ?? ''}`}
      >
        <h3 className="text-center text-[27px] mb-7.5 font-medium">
          {t('signin.form.welcomeBack')}
        </h3>
        <Controller
          control={control}
          name="login"
          render={({ field }) => (
            <div className="flex flex-col gap-y-2.5 mb-5">
              <Label htmlFor={field.name}>Username/Email address</Label>
              <div>
                <Input
                  {...field}
                  error={!!errors.login}
                  id={field.name}
                  inputSize={'lg'}
                  placeholder="Username/Email address"
                />
                {errors.login && (
                  <p className="text-destructive text-xs mt-1">
                    {errors?.login?.message}
                  </p>
                )}
              </div>
            </div>
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <div className="flex flex-col gap-y-2.5 mb-3">
              <Label htmlFor={field.name}>{t('common.form.password')}</Label>
              <div>
                <Input
                  {...field}
                  error={!!errors.password}
                  id={field.name}
                  inputSize={'lg'}
                  placeholder={t('common.form.password')}
                  type="password"
                />
                {errors.password && (
                  <p className="text-destructive text-xs mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>
          )}
        />
        <div className="flex items-center justify-between mb-3">
          <Button
            className="p-0 font-semibold"
            type="button"
            variant={'link'}
            onClick={onOpenForgotEmail}
          >
            {t('common.form.forgotEmail')}?
          </Button>
          <Button
            className="p-0 font-semibold"
            type="button"
            variant={'link'}
            onClick={onOpenForgotPassword}
          >
            {t('common.form.forgotPassword')}?
          </Button>
        </div>
        <Button
          className="w-full font-bold mb-5 text-base"
          loading={isSubmitting}
          size="lg"
          type="submit"
          variant="gradient"
        >
          {t('common.form.signInBtn')}
        </Button>
        {!hideRedirectToSignUp ? (
          <p className="text-sm text-center font-[Poppins] font-medium">
            {t('signup.form.dontHaveAccount')}{' '}
            <span
              className="underline cursor-pointer hover:text-accent-foreground"
              onClick={() => navigate('/signup')}
            >
              {t('signup.form.signUpToday')}
            </span>
          </p>
        ) : null}
      </Card>
    </form>
  );
};
